import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, RotateCcw } from "lucide-react";
import ProtocolEditorPanel, { validateProtocol, normalizeProtocolForForm } from "@/components/ProtocolEditorPanel";

const emptyPhase = () => ({
  id: `fase-${Math.random().toString(36).slice(2, 8)}`,
  name: "Nova fase", day_start: 1, day_end: 30,
  objectives: [], supplementation: [], expected_symptoms: "",
});

const DEFAULT_QUESTIONS = [
  { key: "energy", label: "Como está sua energia?", description: "0 = exausto(a) · 10 = energia ótima", min: 0, max: 10 },
  { key: "sleep", label: "Como está seu sono?", description: "0 = péssimo · 10 = restaurador", min: 0, max: 10 },
  { key: "intestine", label: "Como está seu intestino?", description: "0 = muito ruim · 10 = regulado", min: 0, max: 10 },
  { key: "adherence", label: "Qual sua adesão ao protocolo?", description: "0 = não seguiu · 10 = seguiu 100%", min: 0, max: 10 },
];

const emptyProtocol = () => ({
  name: "", category: "", duration_days: 90, description: "",
  indications: [], phases: [emptyPhase()],
  checkin_questions: [...DEFAULT_QUESTIONS],
  education: { html: "", links: [], pdfs: [] },
  active: true,
});

export default function AdminProtocolsPage() {
  const [protocols, setProtocols] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/protocols");
      setProtocols(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(emptyProtocol()); };
  const openEdit = (p) => { setEditing(normalizeProtocolForForm(p)); };
  const close = () => setEditing(null);

  const save = async () => {
    const err = validateProtocol(editing);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        category: editing.category,
        duration_days: Number(editing.duration_days),
        description: editing.description,
        indications: editing.indications || [],
        phases: editing.phases.map((ph) => ({
          id: ph.id,
          name: ph.name,
          day_start: Number(ph.day_start),
          day_end: Number(ph.day_end),
          objectives: ph.objectives || [],
          supplementation: ph.supplementation || [],
          expected_symptoms: ph.expected_symptoms || "",
        })),
        checkin_questions: editing.checkin_questions || [],
        checklist_items: editing.checklist_items || [],
        symptom_items: editing.symptom_items || [],
        education: editing.education || { html: "", links: [], pdfs: [], items: [] },
        active: editing.active !== false,
      };
      if (editing.id) {
        await api.patch(`/admin/protocols/${editing.id}`, payload);
        toast.success("Protocolo atualizado.");
      } else {
        await api.post("/admin/protocols", payload);
        toast.success("Protocolo criado.");
      }
      close();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar protocolo.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir o protocolo "${p.name}"? Pacientes ativos nele voltam ao estado sem protocolo.`)) return;
    try {
      await api.delete(`/admin/protocols/${p.id}`);
      toast.success("Protocolo removido.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir.");
    }
  };

  const restore = async (p) => {
    try {
      await api.patch(`/admin/protocols/${p.id}`, { active: true });
      toast.success("Protocolo reativado.");
      await load();
    } catch (_) { toast.error("Erro ao reativar."); }
  };

  return (
    <div className="space-y-6" data-testid="admin-protocols-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3">Biblioteca clínica</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Protocolos</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Crie, edite e remova protocolos, fases e perguntas de check-in. Alterações são aplicadas imediatamente aos pacientes ativos.
          </p>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-protocol-btn"
        >
          <Plus className="w-4 h-4" /> Novo protocolo
        </button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-4 py-3 overline text-muted-foreground">Protocolo</th>
              <th className="px-4 py-3 overline text-muted-foreground">Categoria</th>
              <th className="px-4 py-3 overline text-muted-foreground">Duração</th>
              <th className="px-4 py-3 overline text-muted-foreground">Fases</th>
              <th className="px-4 py-3 overline text-muted-foreground">Perguntas</th>
              <th className="px-4 py-3 overline text-muted-foreground">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {protocols.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors" data-testid={`admin-protocol-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{p.name}</div>
                  {p.description && <div className="text-xs text-muted-foreground line-clamp-1 max-w-md">{p.description}</div>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{p.category}</td>
                <td className="px-4 py-3">{p.duration_days} dias</td>
                <td className="px-4 py-3">{(p.phases || []).length}</td>
                <td className="px-4 py-3">{(p.checkin_questions || []).length}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block overline px-2 py-0.5 rounded-sm border ${p.active !== false ? "border-success/40 text-success bg-success/5" : "border-border text-muted-foreground bg-muted"}`}>
                    {p.active !== false ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => openEdit(p)} className="p-2 rounded-sm hover:bg-muted transition-colors" data-testid={`edit-protocol-${p.id}`} title="Editar">
                      <Pencil className="w-4 h-4 text-primary" />
                    </button>
                    {p.active !== false ? (
                      <button onClick={() => remove(p)} className="p-2 rounded-sm hover:bg-muted transition-colors" data-testid={`delete-protocol-${p.id}`} title="Excluir">
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </button>
                    ) : (
                      <button onClick={() => restore(p)} className="p-2 rounded-sm hover:bg-muted transition-colors" data-testid={`restore-protocol-${p.id}`} title="Reativar">
                        <RotateCcw className="w-4 h-4 text-success" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {protocols.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">Nenhum protocolo cadastrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close} data-testid="protocol-modal">
          <div className="bg-card border border-border rounded-sm p-0 max-w-3xl w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* header */}
            <div className="flex items-start justify-between p-6 border-b border-border">
              <div>
                <div className="overline text-gold mb-1">{editing.id ? "Editar protocolo" : "Novo protocolo"}</div>
                <h3 className="brand-wordmark uppercase text-xl font-bold text-primary">{editing.name || "Sem nome"}</h3>
              </div>
              <button onClick={close} className="p-1 hover:bg-muted rounded-sm" data-testid="close-protocol-modal">
                <X className="w-4 h-4" />
              </button>
            </div>

            <ProtocolEditorPanel value={editing} onChange={setEditing} showActiveToggle />

            {/* footer */}
            <div className="border-t border-border p-4 flex gap-2 justify-end">
              <button onClick={close} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors" data-testid="cancel-protocol-btn">Cancelar</button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
                data-testid="save-protocol-btn">
                <Save className="w-4 h-4" /> {saving ? "Salvando…" : "Salvar protocolo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
